import React, { useState, useEffect } from 'react';
import { Modal } from './components/ui';
import OrderDetailsWindow from './components/OrderDetails/OrderDetailsWindow';
// Providers agora ficam no main.tsx
import { initAuth } from './lib/auth';
// ConnectivityProvider agora também está no main.tsx
import ConnectivityBanner from './components/ConnectivityBanner';
import SsoStatusBanner from './components/SsoStatusBanner';
import { runStorageMigrations } from './lib/migrations/storageMigrations';
import { NotificationRenderer, ConfirmationRenderer } from './components/feedback';
import { TokenService } from './lib/tokenService';
import { corelCommunication } from './lib/corel/communication';
import { ensureSessionHelloOnce } from './lib/sessionHandshake';
import { ToolsMenu } from './components/ToolsMenu';
import { extractInitialToken, getInitialHashRoutePath } from './lib/initialHash';
import { setShortSsoToken, setBaseBearer, exchangeForSsoToken } from './lib/toolsSso';
import { handleBootstrapData, type BootstrapData } from './lib/corel/bootstrapHandler';
import { api } from './lib/api';
import { validateMeResponse } from './lib/auth/validators';
import { MontaTechPage } from './components/Tools/MontaTech';
import { QuotationCalculatorPage } from './components/Tools/QuotationCalculator';
import { sendToTerminal, terminalLog, logger } from './lib/terminalLogger';
import { usePermissions } from './contexts/PermissionContext';
import { useWebSocketConnection } from './contexts/WebSocketContext';
import { initializeCategoriaPastaMap } from './utils/fileNaming';
import { GlobalFooter } from './components/GlobalFooter';
import { SimulationBanner } from './components/Admin/SimulationBanner';
import { MessagesProvider } from './components/Messages/MessagesProvider';
import { OrdersNotificationHandler } from './components/Orders/OrdersNotificationHandler';
import { useCampanhasWebSocket } from './hooks/useCampanhasWebSocket';
import { notify } from './lib/globalNotify';
import { AppRoutes } from './router/AppRoutes';
import { EmbeddedEntryPage } from './components/EmbeddedEntryPage';
import { useNavigate, useLocation } from 'react-router-dom';
import { useExternalNavigation } from './hooks/useExternalNavigation';
import {
  DEFAULT_EMBEDDED_ROUTE,
  EMBEDDED_ENTRY_ROUTE,
  LOGIN_ROUTE,
  getEmbeddedReturnRoute,
  getStoredEmbeddedScreenState,
  getStoredShellMode,
  isEmbeddedRoute,
  isProtectedAppRoute,
  rememberEmbeddedRoute,
  storeEmbeddedScreenState,
  storeShellMode,
  type EmbeddedScreenState,
  type ShellMode
} from './lib/embeddedShell';
import type { WebViewResponse } from './lib/corel/types/messages';
type User = {
  email: string;
  nome: string;
  tipo: string;
};

type ModalConfig = {
  isOpen: boolean;
  title: string;
  content: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
};

type EmbeddedAuthUpdatedDetail = {
  token?: string;
  ud?: string | null;
  frontendUrl?: string | null;
  sessionEpoch?: number;
  tokenVersion?: number;
  dockerId?: string | null;
};

function isEmbeddedBootstrapPath(pathname: string): boolean {
  return isEmbeddedRoute(pathname) || pathname.startsWith('/embedded/');
}

function isToolsDockerPath(pathname: string): boolean {
  return pathname.startsWith('/tools') || pathname.startsWith('/embedded/tools');
}

function isMontaTechStandalonePath(pathname: string): boolean {
  return pathname === '/tools/montatech' || pathname === '/embedded/tools/montatech';
}

function getBrowserPathname(): string {
  return window.location.pathname || '';
}

function getDockerContextPath(routerPathname: string): string {
  const browserPathname = getBrowserPathname();
  if (isToolsDockerPath(browserPathname) || isMontaTechStandalonePath(browserPathname)) {
    return browserPathname;
  }

  const initialHashRoutePath = getInitialHashRoutePath() || '';
  if (isToolsDockerPath(initialHashRoutePath) || isMontaTechStandalonePath(initialHashRoutePath)) {
    return initialHashRoutePath;
  }

  return routerPathname;
}

function buildUrlWithoutSsoParams(): string {
  const cleanedSearch = window.location.search
    .replace(/[?&]token=[^&#]+(&|$)/g, (_m, end) => end === '&' ? '&' : '')
    .replace(/[?&]ud=[^&#]+(&|$)/g, (_m, end) => end === '&' ? '&' : '')
    .replace(/[?&]mode=[^&#]+(&|$)/g, (_m, end) => end === '&' ? '&' : '')
    .replace(/^\?&/, '?')
    .replace(/\?$/, '');

  const currentHash = window.location.hash || '';
  const cleanedHash = currentHash.includes('?')
    ? currentHash
        .replace(/([?&])token=[^&#]+(&|$)/g, (_m, start, end) => (start === '?' && end === '&') ? '?' : (end === '&' ? start : ''))
        .replace(/([?&])ud=[^&#]+(&|$)/g, (_m, start, end) => (start === '?' && end === '&') ? '?' : (end === '&' ? start : ''))
        .replace(/([?&])mode=[^&#]+(&|$)/g, (_m, start, end) => (start === '?' && end === '&') ? '?' : (end === '&' ? start : ''))
        .replace(/\?&/, '?')
        .replace(/\?$/, '')
        .replace(/&&/g, '&')
    : currentHash;
  return `${window.location.pathname}${cleanedSearch}${cleanedHash}` || '/';
}

/**
 * Processa token SSO da URL durante inicialização do App.
 * Mesma lógica do useSsoFragmentLogin, mas executada antes do routing.
 */
async function processSsoTokenOnInit(token: string, ud: string | null): Promise<void> {
  const decodeJwt = (t: string): any => {
    try {
      const [, payload] = t.split('.') as [string, string, string];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json);
    } catch { return null; }
  };

  const claims = decodeJwt(token);
  let bootstrapData: BootstrapData = { accessToken: token };

  if (ud) {
    try {
      const json = atob(ud.replace(/-/g, '+').replace(/_/g, '/'));
      const parsed = JSON.parse(json);
      bootstrapData = {
        accessToken: token,
        user: parsed.usuario ? {
          id: parsed.usuario.id,
          nome: parsed.usuario.nome,
          login: parsed.usuario.login
        } : undefined,
        permissoes: parsed.permissions || [],
        permissionsConfig: parsed.permissionsConfig || []
      };
    } catch {
      if (claims) {
        bootstrapData = {
          accessToken: token,
          user: { id: Number(claims.sub) || 0, nome: String(claims.user || ''), login: String(claims.user || '') },
          permissoes: [],
          permissionsConfig: []
        };
      }
    }
  } else if (claims) {
    bootstrapData = {
      accessToken: token,
      user: { id: Number(claims.sub) || 0, nome: String(claims.user || ''), login: String(claims.user || '') },
      permissoes: [],
      permissionsConfig: []
    };
  }

  // Processar bootstrap (seta token no TokenService, aguarda permissoes do /auth/me)
  const result = await handleBootstrapData(bootstrapData, 'fragment');
  if (!result.success) {
    throw new Error(result.error || 'Falha ao processar bootstrap SSO');
  }

  // Configurar base bearer (JWT principal) para renovações futuras de SSO
  try {
    setBaseBearer(token);
  } catch {}

  // Emitir SSO token real (aud: "tools_sso") usando o JWT como bearer.
  // O endpoint /auth/cdr/sso-token/refresh exige aud: "tools_sso", então precisamos
  // trocar o JWT (aud: "cdr") por um SSO real para que o auto-refresh funcione.
  try {
    const exchanged = await exchangeForSsoToken(token);
    if (!exchanged) {
      // Fallback: usar JWT como SSO (funciona até o primeiro auto-refresh)
      const expiresAt = claims?.exp ? new Date(claims.exp * 1000).toISOString() : undefined;
      setShortSsoToken(token, expiresAt);
    }
  } catch {
    const expiresAt = claims?.exp ? new Date(claims.exp * 1000).toISOString() : undefined;
    setShortSsoToken(token, expiresAt);
  }

  // Buscar dados completos (permissões) via /auth/me (timeout 5s)
  try {
    const meResponse = await Promise.race([
      api('/auth/me', { method: 'GET' }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout 5s ao buscar /auth/me')), 5000)
      )
    ]);

    const validation = validateMeResponse(meResponse, token);
    if (validation.success) {
      TokenService.setUserData(validation.normalized);
      try { window.dispatchEvent(new CustomEvent('auth:updated')); } catch {}
      sendToTerminal('✅ [SSO] Auto-login processado via App.tsx', {
        userId: validation.normalized.data.usuario.id,
        permissionsConfigCount: validation.normalized.data.permissionsConfig?.length || 0,
      });
    } else {
      sendToTerminal('⚠️ [SSO] Resposta /auth/me inválida, usando dados do bootstrap', {
        error: validation.error,
      });
    }
  } catch (apiErr) {
    sendToTerminal('⚠️ [SSO] Falha ao buscar /auth/me, usando dados do bootstrap', {
      error: apiErr instanceof Error ? apiErr.message : String(apiErr),
    });
  }
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, role } = usePermissions();
  const { enable: enableWebSocket, isEnabled: isWebSocketEnabled } = useWebSocketConnection();

  // ✅ OTIMIZAÇÃO: Habilitar WebSocket apenas após autenticação confirmada
  useEffect(() => {
    if (userInfo?.id && !isWebSocketEnabled) {
      logger.info('🔌 [APP] Usuário autenticado, habilitando WebSocket', { userId: userInfo.id });
      enableWebSocket();
    }
  }, [userInfo?.id, isWebSocketEnabled, enableWebSocket]);

  // ✅ PERFORMANCE: Prefetch de rotas lazy mais acessadas após auth
  // Baixa chunks em background ~2s após login — navegação fica instantânea
  useEffect(() => {
    if (!userInfo?.id) return;
    const timer = setTimeout(() => {
      import('./components/Dashboard/DashboardPage').catch(() => {});
      import('./components/AdminPage').catch(() => {});
      import('./components/pages/CampanhasListPage').catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [userInfo?.id]);

  // WebSocket para notificações de campanhas
  useCampanhasWebSocket(userInfo?.id, {
    onNewCampanha: (campanha) => {
      notify({
        type: 'info',
        title: '🎮 Nova Campanha Disponível!',
        message: `${campanha.titulo} - Participe e concorra a prêmios!`,
        sticky: true,
        actions: [{
          label: 'Participar',
          onClick: () => {
            navigate('/campanhas');
          }
        }]
      });
    },
    onCampanhaActivated: (campanha) => {
      notify({
        type: 'info',
        title: '✅ Campanha Ativada!',
        message: `${campanha.titulo} está disponível para participação.`,
        sticky: true,
        actions: [{
          label: 'Ver Campanhas',
          onClick: () => {
            navigate('/campanhas');
          }
        }]
      });
    }
  });
  
  // ✅ DERIVAR user dos dados centralizados (compatibilidade)
  const user: User | null = userInfo ? {
    email: userInfo.nome, // Usar nome como email para compatibilidade
    nome: userInfo.nome,
    tipo: role
  } : null;
  
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showNewQuotation, setShowNewQuotation] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<{ pedidoId: string; itemId: string; empresa: string } | null>(null);
  const [modal, setModal] = useState<ModalConfig>({
    isOpen: false,
    title: '',
    content: null,
    size: 'md'
  });
  const [initializing, setInitializing] = useState(true);
  const [shellMode, setShellMode] = useState<ShellMode>(() =>
    isEmbeddedBootstrapPath(location.pathname) ? 'embedded' : getStoredShellMode()
  );
  const [embeddedScreenState, setEmbeddedScreenState] = useState<EmbeddedScreenState>(() =>
    isEmbeddedBootstrapPath(location.pathname) ? 'booting' : getStoredEmbeddedScreenState()
  );
  const isEmbeddedMode = shellMode === 'embedded';
  const shouldShowEmbeddedOverlay =
    isEmbeddedMode &&
    embeddedScreenState !== 'authenticated' &&
    location.pathname !== EMBEDDED_ENTRY_ROUTE &&
    isProtectedAppRoute(location.pathname);

  useEffect(() => {
    if (isEmbeddedBootstrapPath(location.pathname)) {
      if (shellMode !== 'embedded') {
        setShellMode('embedded');
      }
      storeShellMode('embedded');
      storeEmbeddedScreenState(embeddedScreenState);
      return;
    }

    if (location.pathname === LOGIN_ROUTE) {
      if (shellMode !== 'browser') {
        setShellMode('browser');
      }
      storeShellMode('browser');
      storeEmbeddedScreenState('authenticated');
    }
  }, [embeddedScreenState, location.pathname, shellMode]);

  useEffect(() => {
    if (isEmbeddedMode) {
      storeEmbeddedScreenState(embeddedScreenState);
    }
  }, [embeddedScreenState, isEmbeddedMode]);

  useEffect(() => {
    if (isEmbeddedMode && isProtectedAppRoute(location.pathname)) {
      rememberEmbeddedRoute(location.pathname);
    }
  }, [isEmbeddedMode, location.pathname]);

  // 🔍 DEBUG: Rastrear mudanças em showOrderDetails/selectedOrder
  useEffect(() => {
    sendToTerminal('🔄 [APP:STATE] showOrderDetails/selectedOrder CHANGED', {
      showOrderDetails,
      selectedOrder: selectedOrder ? {
        pedidoId: selectedOrder.pedidoId,
        itemId: selectedOrder.itemId,
      } : null,
      locationPath: location.pathname,
    });
  }, [showOrderDetails, selectedOrder, location.pathname]);

  // Hook: External navigation events (C#, WebSocket, etc.)
  useExternalNavigation(setSelectedOrder, setShowOrderDetails);

  const applyEmbeddedLauncherRequestState = (
    response: WebViewResponse,
    fallbackState: EmbeddedScreenState
  ) => {
    if (!isEmbeddedMode) {
      return;
    }

    if (response.status === 'erro') {
      setEmbeddedScreenState('waiting_launcher_login');
      return;
    }

    switch (response.message) {
      case 'launcher_launch_requested':
        setEmbeddedScreenState('opening_launcher');
        return;
      case 'auth_reused':
      case 'login_requested':
      case 'logout_detected':
      case 'reauth_in_progress':
        setEmbeddedScreenState('reconnecting');
        return;
      case 'launcher_unavailable':
        setEmbeddedScreenState('waiting_launcher_login');
        return;
      default:
        setEmbeddedScreenState(fallbackState);
        return;
    }
  };

  const requestLauncherLogin = (reason: string, nextState: EmbeddedScreenState = 'reconnecting') => {
    const requestedAt = Date.now();

    if (isEmbeddedMode) {
      setEmbeddedScreenState(nextState);
    }

    try {
      terminalLog.debug('[APP] launcher_login_request', {
        reason,
        timestamp: requestedAt,
        shellMode
      });
      corelCommunication.sendActionGuarded('session:logout', {
        reason,
        timestamp: requestedAt
      }, (response) => {
        terminalLog.debug('[APP] launcher_login_response', {
          reason,
          requestId: response.requestId,
          status: response.status,
          message: response.message ?? null
        });
        applyEmbeddedLauncherRequestState(response, nextState);
      });
    } catch (error) {
      logger.warn('Erro ao solicitar login ao Launcher:', error);
      if (isEmbeddedMode) {
        setEmbeddedScreenState('waiting_launcher_login');
      }
    }
  };

  // Escutar evento sso:logout injetado pelo addon C# (polling de logout do Launcher)
  useEffect(() => {
    const handleSsoLogout = (e: CustomEvent<{ reason?: string; state?: EmbeddedScreenState | 'locked' }>) => {
      logger.warn('🔒 [APP] sso:logout detectado — Launcher deslogou', e.detail);

      const nextEmbeddedState =
        e.detail?.state === 'reconnecting' ? 'reconnecting' : 'waiting_launcher_login';

      if (isEmbeddedMode) {
        if (isProtectedAppRoute(location.pathname)) {
          rememberEmbeddedRoute(location.pathname);
        }

        if (nextEmbeddedState !== 'reconnecting') {
          TokenService.clearTokens();
        }

        setEmbeddedScreenState(nextEmbeddedState);
        return;
      }

      TokenService.clearTokens();
      navigate(LOGIN_ROUTE, { replace: true });
    };

    window.addEventListener('sso:logout', handleSsoLogout as EventListener);
    return () => window.removeEventListener('sso:logout', handleSsoLogout as EventListener);
  }, [isEmbeddedMode, location.pathname, navigate]);

  useEffect(() => {
    const handleSsoAuthUpdated = async (event: CustomEvent<EmbeddedAuthUpdatedDetail>) => {
      const token = event.detail?.token;
      const ud = event.detail?.ud ?? null;
      const sessionEpoch = event.detail?.sessionEpoch ?? 0;
      const tokenVersion = event.detail?.tokenVersion ?? 0;

      if (!token) {
        logger.warn('⚠️ [APP] sso:auth-updated recebido sem token');
        return;
      }

      // Tools Docker: apenas processar token, sem alterar shell mode ou navegar
      const browserPathname = getBrowserPathname();
      const dockerContextPath = getDockerContextPath(location.pathname);
      const isToolsPath = isToolsDockerPath(dockerContextPath);

      if (isToolsPath && browserPathname !== location.pathname) {
        logger.info('🔧 [APP] SSO usando browser path do docker tools', {
          browserPathname,
          routerPathname: location.pathname,
          dockerContextPath
        });
      }

      try {
        if (sessionEpoch > 0) {
          (window as any).sessionEpoch = sessionEpoch;
        }
        if (tokenVersion > 0) {
          (window as any).tokenVersion = tokenVersion;
        }

        if (!isToolsPath) {
          storeShellMode('embedded');
          setShellMode('embedded');
          setEmbeddedScreenState('reconnecting');
        }

        await processSsoTokenOnInit(token, ud);

        if (!isToolsPath) {
          setEmbeddedScreenState('authenticated');

          if (location.pathname === EMBEDDED_ENTRY_ROUTE || !isProtectedAppRoute(location.pathname)) {
            navigate(getEmbeddedReturnRoute(DEFAULT_EMBEDDED_ROUTE), { replace: true });
          }
        }
      } catch (error) {
        logger.error('❌ [APP] Falha ao aplicar sso:auth-updated', error);
        if (!isToolsPath) {
          setEmbeddedScreenState('waiting_launcher_login');
        }
      }
    };

    window.addEventListener('sso:auth-updated', handleSsoAuthUpdated as EventListener);
    return () => window.removeEventListener('sso:auth-updated', handleSsoAuthUpdated as EventListener);
  }, [location.pathname, navigate]);

  // Monitorar mudanças em userInfo para evitar estado inconsistente
  useEffect(() => {
    // Se user se tornar null após login, redirecionar para login
    if (!user && !initializing) {
      if (isEmbeddedMode) {
        if (isProtectedAppRoute(location.pathname)) {
          rememberEmbeddedRoute(location.pathname);
        }
        setEmbeddedScreenState((currentState) => {
          if (currentState !== 'reconnecting' && currentState !== 'opening_launcher') {
            TokenService.clearTokens();
            return 'waiting_launcher_login';
          }

          return currentState;
        });
        return;
      }

      if (location.pathname === LOGIN_ROUTE || isToolsDockerPath(getDockerContextPath(location.pathname))) {
        return;
      }
      logger.warn('⚠️ [APP] User se tornou null durante sessão, redirecionando para login');
      TokenService.clearTokens();
      navigate(LOGIN_ROUTE, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname, initializing, isEmbeddedMode, navigate, embeddedScreenState]);

  // ✅ Limpar estado crítico quando role mudar (navegação tratada por useExternalNavigation)
  useEffect(() => {
    const handleRoleChange = (event: CustomEvent<UserRole>) => {
      const newRole = event.detail;
      logger.info('🔄 [APP] Role alterado, limpando estado crítico', { newRole });

      // Limpar estado de pedidos/ordens
      setSelectedOrder(null);
      setShowOrderDetails(false);
      setShowNewOrder(false);

      // Fechar modais
      setModal({ isOpen: false, title: '', content: null, size: 'md' });

      // Notificar usuário
      notify({
        type: 'success',
        title: 'Modo Alterado',
        message: `Interface alterada para ${newRole.toUpperCase()}`,
        duration: 3000
      });

      // Navegação para /main tratada por useExternalNavigation hook
    };

    window.addEventListener('role:changed', handleRoleChange as EventListener);
    return () => window.removeEventListener('role:changed', handleRoleChange as EventListener);
  }, []);

  // Navegação para pedidos via eventos customizados tratada por useExternalNavigation hook

  // Inicializa autenticação ao carregar o app
  useEffect(() => {
    const initialize = async () => {
      const browserPathname = getBrowserPathname();
      const initialDockerPath = getDockerContextPath(location.pathname);
      const initialPath = initialDockerPath || getInitialHashRoutePath() || location.pathname;
      const initialEmbeddedMode = isEmbeddedBootstrapPath(initialPath) || getStoredShellMode() === 'embedded';
      const __b = (l: string) => console.log(`⏱️ [APP_BENCH] ${l}: ${(performance.now() - ((window as any).__BOOT_START || performance.now())).toFixed(0)}ms`);
      try {
        __b('initialize() start');
        if (
          (isToolsDockerPath(browserPathname) || isMontaTechStandalonePath(browserPathname)) &&
          browserPathname !== location.pathname
        ) {
          logger.info('🔧 [APP] Browser path do docker prevaleceu na inicialização', {
            browserPathname,
            routerPathname: location.pathname,
            initialDockerPath,
            initialHashRoutePath: getInitialHashRoutePath()
          });
        }

        // Migrações de storage (legado -> novo padrão)
        runStorageMigrations();
        __b('After runStorageMigrations');

        if (initialEmbeddedMode) {
          setShellMode('embedded');
          setEmbeddedScreenState('booting');
          storeShellMode('embedded');
        }

        // ✅ SSO: Processar token do Launcher via URL (query string ou hash)
        // O addon C# injeta ?token=JWT&ud=Base64 ao abrir o WebView
        const { token: ssoToken, ud: ssoUd } = extractInitialToken();
        if (ssoToken) {
          logger.info('🔐 [APP] SSO token detectado na URL, processando auto-login...');
          try {
            await processSsoTokenOnInit(ssoToken, ssoUd);
          } catch (ssoErr) {
            logger.warn('⚠️ [APP] Falha ao processar SSO token, seguindo fluxo normal', ssoErr);
            try {
              window.dispatchEvent(new CustomEvent('sso:init-failed', {
                detail: {
                  reason: ssoErr instanceof Error ? ssoErr.message : String(ssoErr),
                  reasonCode: 'sso_token_failed',
                  severity: 'warning',
                  expected: false
                }
              }));
            } catch {}
          }
          // Limpar token da URL
          try {
            window.history.replaceState({}, document.title, buildUrlWithoutSsoParams());
          } catch {}
        }

        // Tenta restaurar a sessão com refresh token
        __b('Before initAuth');
        const isAuthenticated = await initAuth();
        __b(`After initAuth (authenticated=${isAuthenticated})`);
        
        if (isAuthenticated) {
          // debugLog: sessão restaurada
          
          // Tenta carregar os dados do usuário do storage
          const userData = TokenService.getUserData();
          if (userData?.data?.usuario) {
            // ✅ DADOS GERENCIADOS VIA PermissionContext
            // Removido estado local duplicado
            
            // Inicializar mapeamento de categorias da API (em background)
            initializeCategoriaPastaMap().catch((err) => {
              logger.warn('⚠️ Erro ao inicializar mapeamento de categorias:', err);
            });
            
            // debugLog: usuário e role
            // Tools Docker: NUNCA redirecionar — o App.tsx renderiza <ToolsMenu /> diretamente via isToolsDocker
            const isToolsPath = isToolsDockerPath(initialDockerPath || initialPath);

            if (isToolsPath) {
              // Noop: manter na rota /tools para que o early-return do render() exiba <ToolsMenu />
              logger.info('🔧 [APP] Tools Docker detectado, mantendo rota', { initialPath });
            } else if (initialEmbeddedMode) {
              setEmbeddedScreenState('authenticated');
              const embeddedTarget = isProtectedAppRoute(initialPath)
                ? initialPath
                : getEmbeddedReturnRoute(DEFAULT_EMBEDDED_ROUTE);
              if (location.pathname !== embeddedTarget) {
                navigate(embeddedTarget, { replace: true });
              }
            } else {
              navigate('/main');
            }
          } else {
            // Token válido mas sem dados do usuário: voltar para login de forma segura
            // Tools Docker: NUNCA redirecionar — herdará sessão do Docker principal via SSO
            if (isToolsPath) {
              logger.warn('🔧 [APP] Tools Docker sem userData, mantendo rota /tools', { initialPath });
            } else if (initialEmbeddedMode) {
              setEmbeddedScreenState('waiting_launcher_login');
              navigate(EMBEDDED_ENTRY_ROUTE, { replace: true });
            } else {
              navigate(LOGIN_ROUTE, { replace: true });
            }
          }
        } else {
          // Sem sessão: garantir tela de login
          // Tools Docker: NUNCA redirecionar — ToolsMenu renderiza independente
          if (isToolsPath) {
            logger.warn('🔧 [APP] Tools Docker sem sessão, mantendo rota /tools', { initialPath });
          } else if (initialEmbeddedMode) {
            setEmbeddedScreenState('waiting_launcher_login');
            navigate(EMBEDDED_ENTRY_ROUTE, { replace: true });
          } else {
            navigate(LOGIN_ROUTE, { replace: true });
          }
        }
        // Handshake centralizado: dispara apenas se necessário
        try {
          const already = !!((window as any)?.sessionToken);
          if (!already) {
            sendToTerminal('🔄 SESSION HANDSHAKE INITIATED', {
              timestamp: Date.now(),
              source: 'FRONTEND_APP',
              action: 'session:hello',
              currentUserData: {
                hasSessionToken: !!TokenService.getAccessToken(),
                roleAtMount: role
              },
              note: 'Handshake centralizado - ensureSessionHelloOnce()'
            });
            ensureSessionHelloOnce(3000);
          }
        } catch (error) {
          logger.warn('⚠️ [APP] Erro no handshake de sessão:', error);
        }
      } catch (error) {
        logger.error('❌ Erro ao inicializar autenticação:', error);
      } finally {
        __b('setInitializing(false) — UI unblocked');
        setInitializing(false);
      }
    };
    
    initialize();
  }, []);

  useEffect(() => {
    if (initializing || !isEmbeddedMode || !user) {
      return;
    }

    if (location.pathname === EMBEDDED_ENTRY_ROUTE) {
      setEmbeddedScreenState('authenticated');
      navigate(getEmbeddedReturnRoute(DEFAULT_EMBEDDED_ROUTE), { replace: true });
    }
  }, [initializing, isEmbeddedMode, user, location.pathname, navigate]);

  const handleLogin = (_email: string, _password: string, _saveUser: boolean): { success: boolean; error?: string } => {
    // debugLog: login iniciado
    
    // 🔥 SOLUÇÃO SIMPLES: Sempre usar dados da API, sem dependência de localStorage
    try {
      const userData = TokenService.getUserData();
      if (userData?.data?.usuario) {
        // ✅ DADOS GERENCIADOS VIA PermissionContext
        // Removido setUser - usa userInfo do context
        // debugLog: user data carregado e role
        navigate('/main');
        return { success: true };
      }
    } catch (error) {
      logger.error('Error loading user data from localStorage:', error);
      return {
        success: false,
        error: 'Erro ao carregar dados do usuário do armazenamento local.'
      };
    }
    
    // Se não houver dados no localStorage, retorna erro
    return {
      success: false,
      error: 'Dados do usuário não disponíveis. Integração com API de autenticação necessária.'
    };
  };

  const handleLogout = () => {
    if (isEmbeddedMode) {
      if (isProtectedAppRoute(location.pathname)) {
        rememberEmbeddedRoute(location.pathname);
      }
      TokenService.clearTokens();
      requestLauncherLogin('docker1_logout', 'reconnecting');
      return;
    }
    // Limpa todos os tokens e dados do usuário
    TokenService.clearTokens();
    navigate(LOGIN_ROUTE, { replace: true });
    
    // Notificar C# sobre logout do Docker 1
    try {
      terminalLog.debug('[APP] docker1_logout', { timestamp: Date.now() });
      corelCommunication.sendActionGuarded('session:logout', { 
        reason: 'docker1_logout',
        timestamp: Date.now() 
      });
    } catch (error) {
      logger.warn('Erro ao notificar logout para C#:', error);
    }
  };

  const handleNavigateToMain = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
    navigate('/main');
  };

  // Se o Docker 2 abrir diretamente /tools, renderizar ToolsMenu sem exigir login (herdará sessão via WebView)
  const routerPath = location.pathname || getInitialHashRoutePath() || '';
  const dockerContextPath = getDockerContextPath(routerPath);
  const isToolsDocker = isToolsDockerPath(dockerContextPath);
  const isMontaTechStandalone = isMontaTechStandalonePath(dockerContextPath);
  

  // Exibe uma tela de carregamento enquanto inicializa
  if (initializing) {
    if (isEmbeddedMode || isEmbeddedBootstrapPath(location.pathname)) {
      return (
        <EmbeddedEntryPage
          state="booting"
          onRequestLauncherLogin={() => requestLauncherLogin('embedded_launcher_login', 'opening_launcher')}
          onRetry={() => window.location.reload()}
        />
      );
    }
    console.log(`⏱️ [APP_BENCH] App render — BLOCKED by initializing=true: ${(performance.now() - ((window as any).__BOOT_START || performance.now())).toFixed(0)}ms`);
    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-xl">Inicializando aplicação...</p>
        </div>
      </div>
    );
  }

  if (isMontaTechStandalone) {
    return (
      <div className="h-screen w-full bg-navy-900 flex flex-col overflow-hidden">
        <MontaTechPage />
        <NotificationRenderer />
      </div>
    );
  }

  if (isToolsDocker) {
    return (
      <div className="h-screen w-full bg-navy-900 flex flex-col overflow-hidden">
        <ToolsMenu />
        <NotificationRenderer />
      </div>
    );
  }

  return (
      <>
        <div className="h-screen w-full bg-navy-900 flex flex-col overflow-hidden">
          {/* Banner de simulação de fluxo (admin) */}
          {location.pathname !== LOGIN_ROUTE && location.pathname !== EMBEDDED_ENTRY_ROUTE && <SimulationBanner />}
        <div className="flex-1 flex flex-col min-h-0">

          {/* Pedidos, Export e Campanhas migradas para React Router */}
          <AppRoutes
            user={userInfo ? {
              email: userInfo.login || '',
              nome: userInfo.nome || '',
              tipo: role || 'cliente'
            } : null}
            embeddedEntry={
              <EmbeddedEntryPage
                state={embeddedScreenState}
                onRequestLauncherLogin={() => requestLauncherLogin('embedded_launcher_login', 'opening_launcher')}
                onRetry={() => window.location.reload()}
              />
            }
            onLogout={handleLogout}
            onOpenModal={(title, content, size = 'md') => {
              setModal({ isOpen: true, title, content, size });
            }}
            onNewOrder={() => setShowNewQuotation(true)}
          />

          {/* Campanhas migradas para React Router */}
        </div>

        {/* Footer Global - Visível em todas as páginas (exceto login) */}
        {location.pathname !== LOGIN_ROUTE && location.pathname !== '/' && location.pathname !== EMBEDDED_ENTRY_ROUTE && userInfo && (
          <GlobalFooter
            onLogout={handleLogout}
            logoutLabel={isEmbeddedMode ? 'Trocar conta no Launcher' : undefined}
          />
        )}

        {shouldShowEmbeddedOverlay && (
          <EmbeddedEntryPage
            state={embeddedScreenState}
            onRequestLauncherLogin={() => requestLauncherLogin('embedded_launcher_login', 'opening_launcher')}
            onRetry={() => window.location.reload()}
            presentation="overlay"
          />
        )}

        <Modal 
          isOpen={modal.isOpen}
          title={modal.title}
          size={modal.size}
          onClose={() => setModal({ ...modal, isOpen: false })}
        >
          {modal.content}
        </Modal>

        {/* ✅ OPÇÃO A: Manter OrderDetails montado mas oculto durante exportação */}
        {/* Isso preserva todo o estado (monitor, objetos, etc.) e permite "Voltar" instantâneo */}
        {/* 🔍 DEBUG: Log da condição de renderização */}
        {(() => {
          const shouldRender = showOrderDetails && selectedOrder;
          if (!shouldRender && (showOrderDetails || selectedOrder)) {
            // Log apenas quando parcialmente true (indica problema de estado)
            sendToTerminal('⚠️ [APP:RENDER] OrderDetails NÃO renderizado (condição parcial)', {
              showOrderDetails,
              hasSelectedOrder: !!selectedOrder,
            });
          }
          return null;
        })()}
        {showOrderDetails && selectedOrder && (
          <div style={{ display: location.pathname === '/exportar' ? 'none' : 'block' }}>
            <OrderDetailsWindow
              pedidoId={selectedOrder.pedidoId}
              itemId={selectedOrder.itemId}
              empresa={selectedOrder.empresa}
              onClose={() => {
                sendToTerminal('🔴 [APP] onClose CALLED - fechando OrderDetails', {
                  showOrderDetails,
                  selectedOrder: selectedOrder?.pedidoId,
                });
                setShowOrderDetails(false);
                setSelectedOrder(null);
              }}
              onNavigateToMain={handleNavigateToMain}
              onNavigateToExport={() => {
                // 🔍 DEBUG: Este log DEVE aparecer quando a navegação for chamada
                sendToTerminal('🚀 [APP] onNavigateToExport CALLED!', {
                  currentPath: location.pathname,
                  showOrderDetails,
                  hasSelectedOrder: !!selectedOrder,
                });
                console.log('🚀 [APP] onNavigateToExport CALLED! (console.log)');

                // ✅ NÃO limpar estado - OrderDetails fica oculto mas montado
                // Apenas navegar para /exportar
                sendToTerminal('🚀 [APP] Navegando para /exportar (OrderDetails mantido em background)');
                navigate('/exportar');
              }}
            />
          </div>
        )}
        {showNewQuotation && (
          <QuotationCalculatorPage
            onBack={() => setShowNewQuotation(false)}
          />
        )}
        <ConnectivityBanner />
        <SsoStatusBanner />
        <NotificationRenderer />
        <ConfirmationRenderer />
        {/* MessagesProvider só funciona se usuário estiver autenticado */}
        {user && (
          <>
            <MessagesProvider />
            <OrdersNotificationHandler />
          </>
        )}
        </div>
      </>
  );
}

export default App;
